<template>
  <div class="card mb-4">
    <div class="card-header pb-0">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h6>店铺列表</h6>
          <p class="text-xs text-secondary">按商户归集，点击商户行可展开/收起该商户下的所有店铺</p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <input
            v-model="searchText"
            type="text"
            class="form-control form-control-sm"
            placeholder="搜索店铺名称/ID"
            style="width: 200px;"
          />
          <input
            v-model="searchDeliverId"
            type="number"
            class="form-control form-control-sm"
            placeholder="商户ID"
            style="width: 120px;"
          />
          <button class="btn btn-sm btn-outline-primary" @click="doSearch">搜索</button>
          <button class="btn btn-sm btn-outline-secondary" @click="resetSearch" v-if="searchText || searchDeliverId">重置</button>
        </div>
      </div>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">加载中...</span></div>
      </div>
      <div v-else-if="merchantGroups.length === 0" class="text-center text-secondary py-4">暂无店铺数据</div>
      <div v-else>
        <div v-for="(group, gIndex) in paginatedGroups" :key="group.deliver_id" class="border-bottom">
          <!-- 商户折叠头 -->
          <div
            class="d-flex align-items-center justify-content-between px-3 py-2"
            style="cursor: pointer; background: #f8f9fa;"
            @click="toggleGroup(gIndex)"
          >
            <div class="d-flex align-items-center">
              <i :class="group.expanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="me-2 text-secondary text-xs"></i>
              <div>
                <h6 class="mb-0 text-sm">{{ displayNameMap[group.deliver_id] || ('商户#' + group.deliver_id) }}</h6>
                <p class="text-xs text-secondary mb-0">ID: {{ group.deliver_id }}</p>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <span class="badge bg-primary me-2">{{ group.shops.length }} 个店铺</span>
            </div>
          </div>
          <!-- 展开的店铺列表 -->
          <div v-if="group.expanded" class="table-responsive p-0">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">店铺ID</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">店铺名称</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">位置</th>
                  <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">创建时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="shop in group.shops" :key="shop.shop_id">
                  <td>
                    <h6 class="mb-0 text-sm px-3 py-2">{{ shop.shop_id }}</h6>
                  </td>
                  <td>
                    <p class="text-xs font-weight-bold mb-0">{{ shop.shop_name || '未命名' }}</p>
                  </td>
                  <td>
                    <p class="text-xs text-secondary mb-0">{{ shop.shop_location || '未知' }}</p>
                  </td>
                  <td class="align-middle text-center">
                    <span class="text-secondary text-xs font-weight-bold">{{ formattedDate(shop.created_date) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-between align-items-center px-4 py-3">
        <div class="text-sm text-gray-700">
          共 {{ merchantGroups.length }} 个商户，{{ totalShops }} 个店铺
        </div>
        <nav aria-label="Page navigation">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)"><i class="fas fa-chevron-left"></i></a>
            </li>
            <li v-for="page in displayedPages" :key="page" class="page-item" :class="{ active: page === currentPage }">
              <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)"><i class="fas fa-chevron-right"></i></a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllShops } from "@/api/shop.js"
import { getMerchantDisplayName } from "@/utils/merchantCache"
import dayjs from "dayjs"

export default {
  name: "shop-list",
  data() {
    return {
      shops: [],
      merchantGroups: [],
      loading: false,
      currentPage: 1,
      groupsPerPage: 10,
      searchText: '',
      searchDeliverId: '',
      displayNameMap: {}, // deliver_id -> 显示名称
    }
  },
  computed: {
    totalShops() {
      return this.merchantGroups.reduce((sum, g) => sum + g.shops.length, 0)
    },
    totalPages() {
      return Math.ceil(this.merchantGroups.length / this.groupsPerPage) || 1
    },
    paginatedGroups() {
      const start = (this.currentPage - 1) * this.groupsPerPage
      return this.merchantGroups.slice(start, start + this.groupsPerPage)
    },
    displayedPages() {
      const displayCount = 5
      let start = Math.max(1, this.currentPage - Math.floor(displayCount / 2))
      let end = Math.min(start + displayCount - 1, this.totalPages)
      if (end - start + 1 < displayCount) {
        start = Math.max(1, end - displayCount + 1)
      }
      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    },
  },
  methods: {
    formattedDate(ts) {
      return ts ? dayjs.unix(ts).format('YYYY/MM/DD HH:mm') : '-'
    },
    toggleGroup(gIndex) {
      this.paginatedGroups[gIndex].expanded = !this.paginatedGroups[gIndex].expanded
    },
    buildMerchantGroups(shopList) {
      const groupMap = {}
      shopList.forEach(shop => {
        const did = shop.deliver_id
        if (!groupMap[did]) {
          groupMap[did] = {
            deliver_id: did,
            deliver_nick_name: shop.deliver_nick_name || '',
            deliver_name: shop.deliver_name || '',
            shops: [],
            expanded: false,
          }
        }
        groupMap[did].shops.push(shop)
      })
      this.merchantGroups = Object.values(groupMap)
      this.fetchDisplayNames()
    },
    async fetchDisplayNames() {
      const ids = this.merchantGroups.map(g => g.deliver_id)
      const results = await Promise.all(ids.map(id => getMerchantDisplayName(id, '商户#' + id).catch(() => '商户#' + id)))
      const map = {}
      ids.forEach((id, i) => { map[id] = results[i] })
      this.displayNameMap = map
    },
    async fetchShops() {
      this.loading = true
      try {
        const resp = await getAllShops({
          keyword: this.searchText || undefined,
          deliver_id: this.searchDeliverId || undefined,
        })
        if (resp.code === 0) {
          this.shops = resp.desc || []
          this.buildMerchantGroups(this.shops)
        } else {
          this.shops = []
          this.merchantGroups = []
        }
      } catch (err) {
        console.error("获取店铺列表失败:", err)
        this.shops = []
        this.merchantGroups = []
      } finally {
        this.loading = false
      }
    },
    doSearch() {
      this.currentPage = 1
      this.fetchShops()
    },
    resetSearch() {
      this.searchText = ''
      this.searchDeliverId = ''
      this.doSearch()
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) this.currentPage = page
    },
  },
  mounted() {
    this.fetchShops()
  },
}
</script>
